import { AutoSaveState } from '@/Model/forms/types';
import { IEnrollApplicationPayload, callOrderObj, guardianModel } from '@/Model/model';
import { getApplicationDetails, getElasticApplicationDetails, getElasticApplicationsIdDetails, getScholarDetails, getUserDetails, saveApplicationForms, saveApplicationFormsInDB, saveApplicationPartSecond } from '@/services/formsService/formsService';
import store from '@/store';
import APP_UTILITIES from '@/utilities/commonFunctions';
import {
  Action,
  Module,
  Mutation,
  VuexModule,
  getModule
} from 'vuex-module-decorators';
import applicationStore from '../applications/applicationStore';
import MutationNames from './mutationNames';
import { getDecodedToken } from '@/services/common/httpClient';

type ConsentAgreement = | {
      [key: string]: {
        name: string;
        required: boolean;
      };
    }
  | undefined;

const {
  SET_ELASTIC_ENROLLMENT_ID,
  LOAD_FORM_DATA_REQUESTED,
  LOAD_FORM_DATA_SUCCEEDED,
  LOAD_ELASTIC_FORM_DATA_REQUESTED,
  LOAD_ELASTIC_FORM_DATA_SUCCEEDED,
  SET_SHOULD_DISABLE_FORM_FIELDS_TRUE,
  SET_SHOULD_DISABLE_FORM_FIELDS_FALSE
} = MutationNames;

@Module({
  namespaced: true,
  name: 'FormsStore',
  store,
  dynamic: true
})
export class FormsModule extends VuexModule {

  protected applicationsList: IEnrollApplicationPayload[] = [] as IEnrollApplicationPayload[];
  public applicationState = { responseStatus: 0 };
  public applicationsCount: number = 0;
  public applicationsParts: any = {};
  public currentPartIndex: number = 0;
  public currentUpdatedFormField: { name: string; value: any; order?: any } = { name: '', value: '', order: '' };
  public structuredFormsList: any = {};
  public elasticEnrollmentId: string = '';
  public firstGuardianModelDetails: guardianModel = {
    email: '', firstName: '', lastName: '', birthdate: '',
    relation: '', primaryGaurdian: '', phone: '', phonetype: '',
    secondaryPhone: '', secondaryPhoneType: '', addressLine1: '',
    addressLine2: '', city: '', state: '', zipCode: '',
    homeLanguage: ''
  };
  public isFetchingForm = false;

  isLoadingFormData: boolean = false;
  isLoadingElasticFormData: boolean = false;

  public currentScholarAge: number | string = '';
  public scholarElasticEnrollmentId: string = '';
  public scholarDetails: any = {};
  public partFirstFields: string[] = [];
  public isFormValididate: boolean = true;
  public formWidgetList: any = [];
  public currentScholarBirthDate: string = '';
  public partTwoSaveFlag: boolean = true;
  public autoSaveState: AutoSaveState = AutoSaveState.NotStarted;
  public isPartOneFetched: boolean = false;
  public currentPrimaryModel: any = { model: '', order: '' };
  public emergencyFields: any = [];
  public dropdownModels: string[] = [];
  public callOrderObj: any = {};
  public callOrderModel: any = [];
  public firstGuardianData: any = {};
  public hideAgeData: any = undefined;
  public modelsList: string[] = [];
  public shouldDisableFormInputFields: boolean = false;
  public fieldsTraversed: any = {
    emergency: {
      emergency_callOrder: [],
      emergency_email: [],
      emergency_relation: [],
      emergencyPhoneNumberType: [],
      emergencyContact: []
    },
    pickups: {
      authorizedContact: [],
      authorizedDate: [],
      authorizedPhoneNumberType: [],
      authorized_relation: [],
    }
  };

  public consentAgreementMap: any = undefined;
    

  get getConsentAgreementMap() {
    return this.consentAgreementMap;
  }

  get modelsData() {
    return this.modelsList;
  }

  get traversedFields() {
    return this.fieldsTraversed;
  }

  get formValidation() {
    return this.isFormValididate;
  }

  get widgetList() {
    return this.formWidgetList;
  }

  get applicationsData() {
    return this.applicationsList;
  }

  get applicationsPartsData() {
    return this.applicationsParts.length
      ? this.applicationsParts[this.currentPartIndex]
      : this.applicationsParts;
  }

  get currentUpdatedField() {
    return this.currentUpdatedFormField;
  }

  get formsList() {
    return this.structuredFormsList;
  }

  get getElasticEnrollmentId() {
    const elasticId = APP_UTILITIES.getCookie('elasticEnrollmentId');
    return this.elasticEnrollmentId
      ? this.elasticEnrollmentId
      : (elasticId
        ? JSON.parse(elasticId)
        : '');
  }

  get firstGuardianModelsList() {
    return this.firstGuardianModelDetails;
  }

  get getCurrentScholarAge() {
    return this.currentScholarAge;
  }

  get currentPartNumber() {
    return this.currentPartIndex;
  }

  get scholarData() {
    return this.scholarDetails;
  }

  get partFirstDisabledFields() {
    return this.partFirstFields;
  }

  get currentScholarBirth() {
    return this.currentScholarBirthDate;
  }

  get partTwoFormsFetchFlag() {
    return this.partTwoSaveFlag;
  }

  get isPartOneFormsFetched() {
    return this.isPartOneFetched;
  }

  get selectedPrimaryModel() {
    return this.currentPrimaryModel;
  }

  get emergencyData() {
    return this.emergencyFields;
  }

  get selectedDropdownModels() {
    return this.dropdownModels;
  }

  get selectedCallOrders() {
    return this.callOrderModel;
  }

  get firstGuardianModels() {
    return this.firstGuardianData;
  }

  get hideAgeProp() {
    return this.hideAgeData;
  }

  @Mutation
  public setBirthDateData(ageData: any) {
    this.hideAgeData = ageData;
  }

  @Action({ commit: 'setBirthDateData' })
  public updateBirthdateData(ageData: any): Promise<Object> {
    return ageData;
  }

  @Mutation
  mutateWidgetList(widget: any) {
    if (widget.length == 0) {
      this.formWidgetList = [];
    }
    else {
      this.formWidgetList.push(widget);
    }
  }

  @Mutation
  mutateSelectedWidgetItem(widget: any, index: number) {
    this.formWidgetList[index] = widget;
  }

  @Mutation
  mutateFormValidate(isValidate: boolean) {
    this.isFormValididate = isValidate;
  }

  @Mutation
  updateScholarData(response: any) {
    this.scholarDetails = response;
  }

  @Mutation
  fetchFormRequested() {
    this.isFetchingForm = true;
  }

  @Mutation
  fetchFormSucceeded() {
    this.isFetchingForm = false;
  }

  @Action({ commit: 'updateScholarData' })
  public async getScholarData(scholarId: string) {
    this.fetchFormRequested();
    const promise = await getScholarDetails(scholarId);
    const result = promise;
    this.fetchFormSucceeded();
    return result.data;
  }

  @Mutation
  updateApplications(response: any) {
    this.applicationsList = response;
    this.applicationsCount = response.count;
    this.applicationsParts = response.parts
      ? response.parts[this.currentPartIndex]
      : [];
  }

  @Mutation
  mutateCurrentPart(partNumber: number) {
    this.currentPartIndex = partNumber;
  }

  @Mutation
  mutateFirstGuardianData(guardianField: { model: string; value: any }) {
    if (!this.firstGuardianData[guardianField.model]) {
      this.firstGuardianData = { ...this.firstGuardianData, [guardianField.model]: guardianField.value };
    }
  }

  @Action({ commit: 'updateApplications' })
  public async getApplicationDetails(applicationId: string) {
    if (!applicationId) {
      return;
    }
    this.LOAD_FORM_DATA_REQUESTED();
    const result = await getApplicationDetails(applicationId);
    this.LOAD_FORM_DATA_SUCCEEDED();
    return result.data;
  }

  @Mutation
  getElasticApplications(response: any) {
    this.applicationsList = response.formsList;
    this.isPartOneFetched = response.isPartOneFetched
      ? response.isPartOneFetched
      : false;
    this.applicationsParts = response.formsList && response.formsList.parts
      ? response.formsList.parts[this.currentPartIndex]
      : [];
    if (response.siteIds) {
      const checkedSites = response.siteIds;
      applicationStore.mutateCheckedSites(checkedSites);
    }
  }

  @Action({ commit: 'getElasticApplications' })
  public async getElasticApplicationDetails(elasticEnrollmentIdId: string) {
    this.LOAD_ELASTIC_FORM_DATA_REQUESTED();
    const promise = getElasticApplicationDetails(elasticEnrollmentIdId);
    const result = await promise;
    this.LOAD_ELASTIC_FORM_DATA_SUCCEEDED();
    return result.data;
  }

  @Action({ commit: 'getElasticApplicationsID' })
  public async getElasticApplicationsIdDetails(scholarId: string) {
    const promise = getElasticApplicationsIdDetails(scholarId);
    const result = await promise;
    return result.data;
  }

  @Mutation
  getElasticApplicationsID(response: any) {
    this.applicationsList = response.source.formsList;
    const applicationData: any = applicationStore.applicationDataInfo;
    if (applicationData.applicationGuid == response.source.formsList.applicationGuid) {
      APP_UTILITIES.setCookie('elasticEnrollmentId', JSON.stringify(response.id), 1);
    }
    this.scholarElasticEnrollmentId = response.id;
    this.applicationsParts = response.source.formsList.parts
      ? response.source.formsList.parts[this.currentPartIndex]
      : [];
    if (response.source.siteIds) {
      const checkedSites = response.source.siteIds;
      applicationStore.mutateCheckedSites(checkedSites);
    }
  }

  @Mutation
  updateCurrentFormField(fieldDetails: { name: string; value: any; order?: any }) {
    this.currentUpdatedFormField = fieldDetails;
  }

  @Action({ commit: 'updateCurrentFormField' })
  public updateCurrentUpdatedField(fieldDetails: { name: string; value: any; order?: any }) {
    return fieldDetails;
  }

  @Mutation
  updateFormsStructure(formData: any) {
    formData = JSON.parse(formData);
    this.structuredFormsList = formData;
  }

  @Mutation
  public saveFormsData(response: any) {
    if (response) {
      APP_UTILITIES.setCookie('elasticEnrollmentId', JSON.stringify(response.data), 1);
    }
    this.elasticEnrollmentId = response.data;
    this.autoSaveState = AutoSaveState.Completed;
  }

  @Mutation
  public setAutoSaveStateToStarted() {
    this.autoSaveState = AutoSaveState.InProgress;
  }

  @Mutation
  public resetAutoSaveState() {
    this.autoSaveState = AutoSaveState.NotStarted;
  }

  @Action({ commit: 'saveFormsData' })
  public async saveApplicationForms(payload: any) {
    this.setAutoSaveStateToStarted();
    const promise = await saveApplicationForms(payload);
    return promise;
  }

  @Action
  public async saveFinalApplicationForms(formData: any) {
    const promise = await saveApplicationFormsInDB(formData);
    return promise;
  }

  @Action
  public async saveFinalSubmitPartSecond(formData: any) {
    const application: any = JSON.parse(APP_UTILITIES.getCookie('outStandingItems') || '');
    const currentIndex: any = JSON.parse(APP_UTILITIES.getCookie('outStandingItemIndex') || '');
    const scholarId = application[currentIndex] && application[currentIndex].scholarId;
    const promise = await saveApplicationPartSecond(scholarId, formData);
    return promise;
  }

  @Mutation
  resetApplication() {
    this.elasticEnrollmentId = '';
    APP_UTILITIES.removeSpecificCookie('elasticEnrollmentId');
    this.applicationsParts = [];
    this.applicationsList = [];
    this.currentUpdatedFormField = { name: '', value: '' };
    this.callOrderModel = [];
  }

  @Mutation
  resetFields() {
    this.fieldsTraversed = {
      emergency: {
        emergency_callOrder: [],
        emergency_email: [],
        emergency_relation: [],
        emergencyPhoneNumberType: [],
        emergencyContact: []
      },
      pickUps: {
        authorizedContact: [],
        authorizedDate: [],
        authorizedPhoneNumberType: [],
        authorized_relation: [],
      }
    };
  }

  @Mutation
  resetFormModels() {
    this.modelsList = [];
  }

  @Mutation
  setConsentAgreementMap(agreement: ConsentAgreement) {
    this.consentAgreementMap = agreement;
  }


  @Mutation
  mutateFormModels(model: string) {
    if (!this.modelsList.includes(model)) {
      this.modelsList.push(model);
    }
  }

  @Mutation
  mutateFormFields(field: any) {
    if (field.key.includes('emergency')) {
      if (!field.gridType) {
        this.fieldsTraversed['emergency'][field.key].push(field.value);
      }
      else if (field.gridType) {
        if (['emergency_firstname', 'emergency_lastname'].includes(field.key)) {
          this.fieldsTraversed['emergency']['emergencyContact'][field.index] = { ...this.fieldsTraversed['emergency']['emergencyContact'][field.index], [field.key]: field.value };
        }
        else if (['emergency_phone', 'emergency_phonetype'].includes(field.key)) {
          this.fieldsTraversed['emergency']['emergencyPhoneNumberType'][field.index] = { ...this.fieldsTraversed['emergency']['emergencyPhoneNumberType'][field.index], [field.key]: field.value };
        }
      }
    }
    else if (field.key.includes('auth')) {
      if (!field.gridType) {
        this.fieldsTraversed['pickUps'][field.key].push(field.value);
      }
      else if (field.gridType) {
        if (['authorized_firstname', 'authorized_lastname'].includes(field.key)) {
          this.fieldsTraversed['pickUps']['authorizedContact'][field.index] = { ...this.fieldsTraversed['pickUps']['authorizedContact'][field.index], [field.key]: field.value };
        }
        else if (['authorized_fromdate', 'authorized_tilldate'].includes(field.key)) {
          this.fieldsTraversed['pickUps']['authorizedDate'][field.index] = { ...this.fieldsTraversed['pickUps']['authorizedDate'][field.index], [field.key]: field.value };
        }
        else if (['authorized_phone', 'authorized_phonetype'].includes(field.key)) {
          this.fieldsTraversed['pickUps']['authorizedPhoneNumberType'][field.index] = { ...this.fieldsTraversed['pickUps']['authorizedPhoneNumberType'][field.index], [field.key]: field.value };
        }
      }
    }
  }

  @Mutation
  saveGuardianDetails(guardianDetails: guardianModel) {

    this.firstGuardianModelDetails.email = guardianDetails.email
      ? guardianDetails.email
      : this.firstGuardianModelDetails.email;
    this.firstGuardianModelDetails.firstName = guardianDetails.firstName
      ? guardianDetails.firstName
      : this.firstGuardianModelDetails.firstName;
    this.firstGuardianModelDetails.lastName = guardianDetails.lastName
      ? guardianDetails.lastName
      : this.firstGuardianModelDetails.lastName;
    this.firstGuardianModelDetails.birthdate = guardianDetails.birthdate
      ? guardianDetails.birthdate
      : this.firstGuardianModelDetails.birthdate;
    this.firstGuardianModelDetails.primaryGaurdian = guardianDetails.primaryGaurdian
      ? guardianDetails.primaryGaurdian
      : this.firstGuardianModelDetails.primaryGaurdian;
    this.firstGuardianModelDetails.relation = guardianDetails.relation
      ? guardianDetails.relation
      : this.firstGuardianModelDetails.relation;
    this.firstGuardianModelDetails.phone = guardianDetails.phone
      ? guardianDetails.phone
      : this.firstGuardianModelDetails.phone;
    this.firstGuardianModelDetails.phonetype = guardianDetails.phonetype
      ? guardianDetails.phonetype
      : this.firstGuardianModelDetails.phonetype;
    this.firstGuardianModelDetails.secondaryPhone = guardianDetails.secondaryPhone
      ? guardianDetails.secondaryPhone
      : this.firstGuardianModelDetails.secondaryPhone;
    this.firstGuardianModelDetails.secondaryPhoneType = guardianDetails.secondaryPhoneType
      ? guardianDetails.secondaryPhoneType
      : this.firstGuardianModelDetails.secondaryPhoneType;
    this.firstGuardianModelDetails.addressLine1 = guardianDetails.addressLine1
      ? guardianDetails.addressLine1
      : this.firstGuardianModelDetails.addressLine1;
    this.firstGuardianModelDetails.addressLine2 = guardianDetails.addressLine2
      ? guardianDetails.addressLine2
      : this.firstGuardianModelDetails.addressLine2;
    this.firstGuardianModelDetails.city = guardianDetails.city
      ? guardianDetails.city
      : this.firstGuardianModelDetails.city;
    this.firstGuardianModelDetails.state = guardianDetails.state
      ? guardianDetails.state
      : this.firstGuardianModelDetails.state;
    this.firstGuardianModelDetails.zipCode = guardianDetails.zipCode
      ? guardianDetails.zipCode
      : this.firstGuardianModelDetails.zipCode;
    this.firstGuardianModelDetails.homeLanguage = guardianDetails.homeLanguage
      ? guardianDetails.homeLanguage
      : this.firstGuardianModelDetails.homeLanguage;


  }

  @Action
  public async getUserDetails(): Promise<Object> {
    
    const { userId }: any = await getDecodedToken();
    const userIdVal: string | null = APP_UTILITIES.getCookie("user_id") || userId;
    const promise = getUserDetails(userIdVal);
    const result = await promise;
    return result as object;
  }

  @Mutation
  resetGuardianDetails() {
    this.firstGuardianModelDetails = {
      email: '', firstName: '', lastName: '', birthdate: '',
      relation: '', primaryGaurdian: '', phone: '', phonetype: '',
      secondaryPhone: '', secondaryPhoneType: '', addressLine1: '',
      addressLine2: '', city: '', state: '', zipCode: '',
      homeLanguage: ''
    };
  }

  @Mutation
  saveCurrentScholarAge(age: number | string) {
    this.currentScholarAge = age;
  }

  @Mutation
  saveCurrentBirthDate(birthDate: string) {
    this.currentScholarBirthDate = birthDate;
  }

  @Mutation
  updatePartFirstFields(fieldName: string) {
    // this.partFirstFields.push(fieldName)
    if (!this.partFirstFields.includes(fieldName)) {
      this.partFirstFields.push(fieldName);
    }
  }

  @Mutation
  updatePartTwoFetchFlag(saveFlag: boolean) {
    this.partTwoSaveFlag = saveFlag;
  }

  @Mutation
  updateCurrentPrimaryModel(model: any) {
    this.currentPrimaryModel = model;
  }

  @Mutation
  mutateEmergencyFields(fieldValue: any) {
    if (!this.emergencyFields.includes(fieldValue)) {
      this.emergencyFields.push(fieldValue);
    }
  }

  @Mutation
  resetEmergencyFields() {
    this.emergencyFields = [];
  }

  @Mutation
  clearDropdownModels(model: string) {
    if (this.dropdownModels.length) {
      const idx = this.dropdownModels.findIndex((currentModel) => currentModel === model);
      if (idx >= 0) {
        this.dropdownModels.splice(idx, 1);
      }
    }
  }

  @Mutation
  mutateDropdownModels(model: string) {
    if (!this.dropdownModels.includes(model)) {
      this.dropdownModels.push(model);
    }
  }

  @Mutation
  resetDropdownModels() {
    this.dropdownModels = [];
  }

  @Mutation
  mutateChangeCallOrder(obj: callOrderObj) {
    this.callOrderObj = obj;
    const keyIndex = this.callOrderModel.findIndex((e: { model: any }) => e.model === this.callOrderObj.model);
    if (keyIndex > -1) {
      const valueIndex = this.callOrderModel.findIndex((e: { value: any }) => e.value === this.callOrderObj.value);
      if (valueIndex > -1) {
        this.callOrderModel[valueIndex].value = this.callOrderModel[keyIndex].value;
      }
      this.callOrderModel[keyIndex] = this.callOrderObj;
    }
    else {
      const valueIndex = this.callOrderModel.findIndex((e: { value: any }) => e.value === this.callOrderObj.value);
      if (valueIndex > -1) {
        this.callOrderModel.splice(valueIndex, 1);
      }
      this.callOrderModel.push(this.callOrderObj);
    }
  }

  @Mutation
  [SET_ELASTIC_ENROLLMENT_ID](id: string) {
    this.elasticEnrollmentId = id;
  }

  @Mutation
  [LOAD_FORM_DATA_REQUESTED]() {
    this.isLoadingFormData = true;
  }

  @Mutation
  [LOAD_FORM_DATA_SUCCEEDED]() {
    this.isLoadingFormData = false;
  }

  @Mutation
  [LOAD_ELASTIC_FORM_DATA_REQUESTED]() {
    this.isLoadingElasticFormData = true;
  }

  @Mutation
  [LOAD_ELASTIC_FORM_DATA_SUCCEEDED]() {
    this.isLoadingElasticFormData = false;
  }

  @Mutation
  [SET_SHOULD_DISABLE_FORM_FIELDS_TRUE]() {
    this.shouldDisableFormInputFields = true;
  }

  @Mutation
  [SET_SHOULD_DISABLE_FORM_FIELDS_FALSE]() {
    this.shouldDisableFormInputFields = false;
  }
}

export default getModule(FormsModule);
